import { useState } from "react";
import { api, ApiError } from "../lib/api";
import { formatDate, formatTime } from "../lib/hooks";
import { Modal } from "./Modal";
import type { Shift } from "../lib/types";

interface MyRequest {
    id: string;
    status: "requested" | "approved" | "declined" | "withdrawn";
    decision_note: string | null;
}

/**
 * What a staff member sees clicking an open shift card in the grid — the
 * same claim/withdraw the below-grid "Open shifts" list already does, just
 * focused on the one shift they clicked rather than a full table.
 */
export const ClaimShiftModal = ({
    shift, myRequest, onClose, onChanged,
}: {
    shift: Shift;
    myRequest: MyRequest | undefined;
    onClose: () => void;
    onChanged: () => void;
}) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const claim = async () => {
        setBusy(true);
        setError(null);
        try {
            await api.post("/shift-requests", { shiftId: shift.id });
            onChanged();
            onClose();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not send the request");
        } finally {
            setBusy(false);
        }
    };

    const withdraw = async () => {
        if (!myRequest) return;
        setBusy(true);
        setError(null);
        try {
            await api.put(`/shift-requests/${myRequest.id}`, { status: "withdrawn" });
            onChanged();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not withdraw the request");
        } finally {
            setBusy(false);
        }
    };

    const spotsLeft = shift.spots - shift.spots_filled;
    const pending = myRequest?.status === "requested";

    return (
        <Modal title="Open shift" onClose={onClose}>
            <dl className="kv">
                <dt>When</dt>
                <dd>{formatDate(shift.starts_at)}, {formatTime(shift.starts_at)}–{formatTime(shift.ends_at)}</dd>
                <dt>Where</dt><dd>{shift.placement_name ?? "—"}</dd>
                <dt>Role</dt><dd>{shift.role_name ?? "—"}</dd>
                <dt>Spots</dt>
                <dd>
                    {shift.spots_filled} of {shift.spots} filled
                    {spotsLeft > 0 && <span className="chip pass" style={{ marginLeft: 6 }}>{spotsLeft} left</span>}
                </dd>
                {shift.notes && (<><dt>Notes</dt><dd>{shift.notes}</dd></>)}
            </dl>

            {error && <div className="error-banner" style={{ marginTop: "0.8rem" }}>{error}</div>}

            <div style={{ marginTop: "1rem" }}>
                {pending ? (
                    <>
                        <p style={{ fontSize: "0.85rem", marginBottom: "0.6rem" }}>
                            <span className="chip warn">Request pending</span> Waiting on a manager to decide.
                        </p>
                        <button className="btn ghost" disabled={busy} onClick={() => void withdraw()}>
                            {busy ? "Withdrawing…" : "Withdraw request"}
                        </button>
                    </>
                ) : myRequest?.status === "declined" ? (
                    <p style={{ fontSize: "0.85rem" }}>
                        <span className="chip block">Declined</span> {myRequest.decision_note ?? ""}
                    </p>
                ) : spotsLeft <= 0 ? (
                    <p style={{ fontSize: "0.85rem", color: "var(--muted)" }}>This shift's spots are already filled.</p>
                ) : (
                    <button className="btn" disabled={busy} onClick={() => void claim()}>
                        {busy ? "Sending…" : "Claim shift"}
                    </button>
                )}
            </div>
        </Modal>
    );
};
